import React, { useState } from 'react';
import { Clock, Calendar, Globe, ChevronDown, AlertTriangle } from 'lucide-react';

/**
 * DateMocker component allows setting a fake date/time for testing purposes
 * @param {function} onApplyFakeDate - Callback function when fake date is applied
 * @param {boolean} bypassCache - Whether to bypass cache when fetching data
 */
const DateMocker = ({ onApplyFakeDate, bypassCache = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleApplyFakeDate = () => {
    let date = document.getElementById('fake_date').value;
    let time = document.getElementById('fake_time').value;
    let tz = document.getElementById('fake_tz').value;
    let fake_date = `${date} ${time} ${tz}`;

    onApplyFakeDate(bypassCache, fake_date);
  };

  return (
    <div className="card overflow-hidden">
      <div 
        className="flex cursor-pointer items-center justify-between border-b border-gray-100 px-5 py-4 transition-colors hover:bg-gray-50/70 dark:border-gray-700 dark:hover:bg-white/[0.03]"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary-50 p-2 dark:bg-primary-900/30">
            <Clock className="h-[18px] w-[18px] text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Date Simulator</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Test with different date and time settings</p>
          </div>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </div>
      
      {isExpanded && (
        <div className="bg-gray-50/70 p-5 dark:bg-gray-900/40">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label htmlFor="fake_date" className="label flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                Date
              </label>
              <div className="relative">
                <input 
                  id="fake_date" 
                  type="date" 
                  className="input" 
                  defaultValue={new Date().toISOString().substring(0, 10)}
                />
              </div>
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="fake_time" className="label flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-gray-400" />
                Time
              </label>
              <input 
                id="fake_time" 
                type="time" 
                className="input" 
                defaultValue={new Date().toISOString().substring(11, 16)}
              />
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="fake_tz" className="label flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-gray-400" />
                Timezone
              </label>
              <div className="relative">
                <select 
                  id="fake_tz" 
                  className="input appearance-none pr-9" 
                  defaultValue={Intl.DateTimeFormat().resolvedOptions().timeZone}
                >
                  {Intl.supportedValuesOf('timeZone').map((tz, index) => (
                    <option key={index}>{tz}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
            
            <div className="flex flex-col justify-end">
              <button 
                className="btn btn-primary w-full"
                onClick={handleApplyFakeDate}
              >
                Apply Date
              </button>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-2.5 rounded-lg border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-500/10">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
              <div>
                <p className="mb-1 text-sm font-semibold text-warning-800 dark:text-warning-400">Test participants only</p>
                <div className="space-y-2 text-xs text-warning-700 dark:text-warning-400/90">
                  <p>
                    Applying a simulated date writes window statuses to the database as if the simulated time were now. Survey availability, due dates, and conditional flows will reflect the simulated time until the next scheduled recalculation re-evaluates them against real time.
                  </p>
                  <p>
                    Do not apply this to real study participants — use a test participant under your sponsor.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1 text-xs text-gray-500 dark:text-gray-400">
              <p className="font-medium text-gray-600 dark:text-gray-300">How to use</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Pick a date, time, and timezone, then click <span className="font-medium">Apply Date</span>.</li>
                <li>The page reloads with surveys, statuses, and due dates as they would appear at that moment.</li>
                <li>To return to normal, refresh without applying — scheduled processing will reset window statuses on its next run.</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateMocker;
